import { useEffect, useRef, useState } from "react"
import { isWcmEditor } from "../../../../config/serverConfig"
import { videoSourceOption } from "../../topStory/windows/contentWindow/videoLiveSourceSelector"
import { LiveVideoPlayer } from "../../ynet1280/liveVideoPlayer/components/liveVideoPlayer"
import { buildAdTag } from "../../../siteWidgets/videoPlayer/videoPlayerAds"
import { dismissVideoTeaser, isVideoTeaserDismissed } from "../utils/videoTeaserDismissStorage"

export interface VideoTeaserFloatingPlayerProps {
    videoSource: videoSourceOption
    videoTitle: string
    mediaVideoId: string
    hideVideoAds?: boolean
    videoAutoPlay?: boolean
    videoMuteSound?: boolean
    isLogoExist?: boolean
    poster?: string
    thisLocation?: string
}

const pushMediaStartAnalytics = () => {
    if (typeof window === "undefined" || !(window as any).dataLayer) {
        return
    }
    try {
        ;(window as any).dataLayer.push({
            event: "media_start",
            media_player_name: "flowplayer",
            media_autoplay: "false",
            source: "video_teaser",
        })
    } catch (e) {
    }
}

const hasCompetingArticleVideo = () => {
    if (typeof document === "undefined") {
        return false
    }
    return !!document.querySelector(".articleVideoComponenta")
}

export const VideoTeaserFloatingPlayer = (props: VideoTeaserFloatingPlayerProps) => {
    const {
        videoSource,
        videoTitle,
        mediaVideoId,
        hideVideoAds = false,
        videoAutoPlay = true,
        videoMuteSound = true,
        isLogoExist = false,
        poster,
        thisLocation = "",
    } = props

    const [isAllowed, setIsAllowed] = useState(false)
    const [isFloatingPlayerVisible, setIsFloatingPlayerVisible] = useState(true)
    const wrapperRef: any = useRef<any>(null)
    const playerInstanceRef: any = useRef<any>(null)
    const listenersBoundRef: any = useRef(false)
    const prerollRequestedRef: any = useRef(false)
    const mediaStartSentRef: any = useRef(false)
    const hideVideoAdsRef: any = useRef(hideVideoAds)

    const fireMediaStartOnce = () => {
        if (mediaStartSentRef.current) {
            return
        }
        mediaStartSentRef.current = true
        pushMediaStartAnalytics()
    }

    const onStartAds = () => {
        const player = playerInstanceRef.current
        if (!player || hideVideoAdsRef.current || prerollRequestedRef.current) {
            return
        }
        if (!player.ads || typeof player.ads.requestAd !== "function") {
            return
        }
        prerollRequestedRef.current = true
        try {
            const adTag = buildAdTag("Preroll", false, true, "fpn")
            try {
                player.pause()
            } catch (e) {
            }
            player.setOpts({ ima: { locale: "he", ads: [{ time: 0, adTag }] } })
            player.ads.requestAd(adTag)
            try {
                player.play()
            } catch (e) {
            }
        } catch (e) {
            prerollRequestedRef.current = false
            try {
                player.play()
            } catch (err) {
            }
        }
    }

    const fireMediaStartOnceRef: any = useRef(fireMediaStartOnce)
    const onStartAdsRef: any = useRef(onStartAds)
    fireMediaStartOnceRef.current = fireMediaStartOnce
    onStartAdsRef.current = onStartAds

    const playerRefForExternal: any = useRef({
        get current() {
            return playerInstanceRef.current
        },
        set current(player: any) {
            playerInstanceRef.current = player
            if (!player || listenersBoundRef.current) {
                return
            }
            listenersBoundRef.current = true
            try {
                player.setOpts({ ima: null })
            } catch (e) {
            }
            const bindListeners = () => {
                const onVolumeChange = () => {
                    if (player.muted) {
                        return
                    }
                    fireMediaStartOnceRef.current()
                    onStartAdsRef.current()
                }
                const onFullscreen = () => {
                    fireMediaStartOnceRef.current()
                }
                const unmuteAd = () => {
                    try {
                        if (player.ads && player.ads.adapter && typeof player.ads.adapter.volume === "function") {
                            player.ads.adapter.volume(1)
                        }
                    } catch (e) {
                    }
                }
                const onAdDone = () => {
                    try {
                        player.setOpts({ muted: false })
                        player.play()
                    } catch (e) {
                    }
                }
                if (player.on) {
                    player.on("volumechange", onVolumeChange)
                    player.on("fullscreenenter", onFullscreen)
                }
                if (player.ads && player.ads.on) {
                    player.ads.on("ad-started", unmuteAd)
                    player.ads.on("ad-completed", onAdDone)
                    player.ads.on("ad-skipped", onAdDone)
                    player.ads.on("ad-error", onAdDone)
                }
            }
            if (typeof setImmediate === "function") {
                setImmediate(bindListeners)
            } else {
                setTimeout(bindListeners, 0)
            }
        },
    }).current

    useEffect(() => {
        hideVideoAdsRef.current = hideVideoAds
    }, [hideVideoAds])

    useEffect(() => {
        if (!isAllowed || !isFloatingPlayerVisible) {
            return
        }
        const el = wrapperRef.current
        if (!el) {
            return
        }
        const onMuteBtnClick = (e: any) => {
            const target = e.target
            if (!target || !target.closest || !target.closest(".mute-btn")) {
                return
            }
            if (hideVideoAdsRef.current || prerollRequestedRef.current) {
                return
            }
            fireMediaStartOnceRef.current()
            onStartAdsRef.current()
        }
        el.addEventListener("click", onMuteBtnClick, true)
        return () => {
            el.removeEventListener("click", onMuteBtnClick, true)
        }
    }, [isAllowed, isFloatingPlayerVisible])

    useEffect(() => {
        if (isWcmEditor()) {
            setIsAllowed(false)
            return
        }
        if (!mediaVideoId || !videoSource || !videoSource.src) {
            setIsAllowed(false)
            return
        }
        if (isVideoTeaserDismissed(mediaVideoId)) {
            setIsAllowed(false)
            return
        }
        if (hasCompetingArticleVideo()) {
            setIsAllowed(false)
            return
        }
        setIsAllowed(true)
    }, [mediaVideoId, videoSource && videoSource.src])

    const onIsFloatingPlayerVisibleChange = (isOpen: boolean) => {
        setIsFloatingPlayerVisible(isOpen)
        if (!isOpen) {
            if (mediaVideoId) {
                dismissVideoTeaser(mediaVideoId)
            }
        }
    }

    if (!isAllowed || !isFloatingPlayerVisible || !videoSource || !videoSource.src) {
        return null
    }

    const teaserVideoSource: videoSourceOption = {
        src: videoSource.src,
        lowResSrc: videoSource.src,
        label: videoSource.label || "",
    }

    return (
        <div ref={wrapperRef} className={`video-teaser-floating-player ${isLogoExist ? "with-bepo-logo" : "without-bepo-logo"}`}>
            <LiveVideoPlayer
                videoTitle={videoTitle}
                liveVideoSource={teaserVideoSource}
                videoAutoPlay={videoAutoPlay}
                videoMuteSound={videoMuteSound}
                videoShowSoundBtn={true}
                videoShowControlPanel={true}
                isMinimizedVisible={false}
                videoType={"bepo"}
                isLogoExist={true}
                hideVideoAds={true}
                poster={poster}
                thisLocation={thisLocation}
                externalIsOpenTrigger={true}
                onExternalIsOpenTriggerChange={onIsFloatingPlayerVisibleChange}
                playerRefForExternal={playerRefForExternal}
            />
        </div>
    )
}

VideoTeaserFloatingPlayer.siteScriptName = "VideoTeaserFloatingPlayer"
